import { useState } from 'react';
import {
  useAdminBanner, useAdminPointValues, useAdminPointsAudit, useSetBanner, useUpdatePointValue,
} from '../../hooks/useQuestAdmin';
import { fmtDateTime } from './questShared';
import {
  BTN, BTN_SECONDARY, Card, ErrorNote, INPUT, Loading,
} from './adminUi';
import { resizePhoto } from './SceneEditor';

// What each activity is worth (the brief's points table, tunable without a
// release), and the audit history of every manual add or remove.

function ValueRow({ v }) {
  const update = useUpdatePointValue();
  const [points, setPoints] = useState(String(v.points));
  const changed = Number(points) !== v.points;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
      <span>
        <span className="block text-sm font-medium text-gray-900">{v.label}</span>
        <span className="block text-xs text-gray-400">{v.activity}</span>
      </span>
      <span className="flex items-center gap-2">
        <input type="number" min="0" value={points} onChange={(e) => setPoints(e.target.value)}
          className={`${INPUT} w-24 text-right`} />
        <button type="button" disabled={!changed || update.isPending}
          onClick={() => update.mutate({ activity: v.activity, points: Number(points), is_active: v.is_active })}
          className={BTN}>
          Save
        </button>
      </span>
      {update.isError ? <div className="w-full"><ErrorNote error={update.error} /></div> : null}
    </li>
  );
}

// The artwork banner across the top of Nano Quest in the app (brief section 2:
// "NANO QUEST (Artwork banner)"). Wide artwork works best — about 3:1.
function Banner() {
  const q = useAdminBanner();
  const save = useSetBanner();
  const [error, setError] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setError(null);
      save.mutate({ image: await resizePhoto(file) });
    } catch (err) { setError(err.message); }
  };
  return (
    <Card className="lg:col-span-2">
      <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Home banner
      </p>
      <div className="space-y-3 p-4">
        <p className="text-xs text-gray-500">
          Shown across the top of Nano Quest in the app. Wide artwork works best, about three times as wide as it is tall.
        </p>
        <Loading q={q} what="the banner" />
        {q.data?.image ? (
          <img src={q.data.image} alt="Nano Quest banner" className="max-h-48 w-full rounded-md object-cover" />
        ) : q.data !== undefined ? (
          <p className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-400">
            No banner — the app shows the Nano Quest heading on its own.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <label className={`${BTN} cursor-pointer`}>
            {q.data?.image ? 'Replace the banner' : 'Upload a banner'}
            <input type="file" accept="image/*" onChange={upload} className="hidden" disabled={save.isPending} />
          </label>
          {q.data?.image ? (
            <button type="button" onClick={() => save.mutate({ image: null })} disabled={save.isPending} className={BTN_SECONDARY}>
              Take it down
            </button>
          ) : null}
        </div>
        {q.data?.updated_by_name ? (
          <p className="text-xs text-gray-400">Last changed by {q.data.updated_by_name} on {fmtDateTime(q.data.updated_at)}</p>
        ) : null}
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        {save.isError ? <ErrorNote error={save.error} /> : null}
      </div>
    </Card>
  );
}

function SettingsTab() {
  const values = useAdminPointValues();
  const audit = useAdminPointsAudit();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Banner />
      <Card>
        <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Point values
        </p>
        <p className="px-4 pt-2 text-xs text-gray-500">
          A change applies to points awarded from now on. Each Quest also has its own points, set on the Quest.
        </p>
        <Loading q={values} what="the point values" />
        <ul className="divide-y divide-gray-100">
          {(values.data || []).map((v) => <ValueRow key={v.activity} v={v} />)}
        </ul>
      </Card>

      <Card>
        <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Manual adjustments
        </p>
        <Loading q={audit} what="the audit history" />
        {audit.data && !audit.data.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">No manual adjustments yet.</p> : null}
        <ul className="divide-y divide-gray-100">
          {(audit.data || []).map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{a.display_name || 'Unknown'}</span>
                <span className="block text-xs text-gray-500">{a.reason}</span>
                <span className="block text-xs text-gray-400">
                  {fmtDateTime(a.created_at)}{a.awarded_by_name ? ` · by ${a.awarded_by_name}` : ''}
                </span>
              </span>
              <span className={`shrink-0 text-sm font-bold tabular-nums ${a.points < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                {a.points > 0 ? '+' : ''}{a.points}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export default SettingsTab;
